import { useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import FormFieldWorkerPool, {
  getInitialWorkerPoolValue,
  getWorkerPoolSubmitValue,
} from "components/FormFields/WorkerPool";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import getIntentProjectRunnerImageTooltip from "shared/IntentProject/getRunnerImageTooltip";
import useCreateIntentProject from "shared/IntentProject/useCreateIntentProject";
import useUpdateIntentProject from "shared/IntentProject/useUpdateIntentProject";
import useSpacesByAction from "shared/Space/useSpacesByAction";
import { Action, IntentProject } from "types/generated";
import { getDrawerFormFix } from "utils/css";

type CreateIntentProjectFormFields = {
  name: string;
  description?: string;
  space: string;
  labels: { value: string }[];
  workerPool: string;
  runnerImage: string;
};

type CreateIntentProjectDrawerProps = {
  project?: IntentProject;
};

const CreateIntentProjectDrawer = createDrawer(({ project }: CreateIntentProjectDrawerProps) => {
  const isEditMode = !!project;
  const drawer = useModal();
  const navigate = useNavigate();
  const { createIntentProject, loading: createLoading } = useCreateIntentProject();
  const { updateIntentProject, loading: updateLoading } = useUpdateIntentProject();
  const { spaces: creatableSpaces, loading: spacesLoading } = useSpacesByAction({
    actions: [Action.IntentProjectCreate],
  });

  const form = useForm<CreateIntentProjectFormFields>({
    defaultValues: {
      name: project?.name || "",
      description: project?.description || "",
      space: project?.space.id || "",
      labels: project?.labels.map((value) => ({ value })) ?? [],
      workerPool: getInitialWorkerPoolValue(project?.workerPool?.id),
      runnerImage: project?.runnerImage ?? "",
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid, dirtyFields },
  } = form;

  const onSubmit: SubmitHandler<CreateIntentProjectFormFields> = useCallback(
    (data) => {
      const labels = data.labels.length > 0 ? data.labels.map((l) => l.value) : [];

      if (isEditMode) {
        updateIntentProject(
          {
            id: project.id,
            name: data.name,
            description: data.description,
            space: data.space,
            labels,
            workerPool: getWorkerPoolSubmitValue(data.workerPool),
            runnerImage: dirtyFields.runnerImage ? data.runnerImage : undefined,
          },
          () => drawer.hide()
        );

        return;
      }

      createIntentProject(
        {
          name: data.name,
          description: data.description,
          space: data.space,
          labels,
          workerPool: getWorkerPoolSubmitValue(data.workerPool),
          runnerImage: data.runnerImage,
        },
        (id) => {
          navigate(`/intent-project/${id}`);
          drawer.hide();
        }
      );
    },
    [
      createIntentProject,
      drawer,
      navigate,
      isEditMode,
      project?.id,
      updateIntentProject,
      dirtyFields.runnerImage,
    ]
  );

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle
              title={isEditMode ? "Edit Intent Project" : "Create new Intent Project"}
            />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody noPadding>
            <DrawerSection gap="x-large">
              <FormField label="Name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter intent project name"
                    error={!!errors?.name}
                    {...register("name", {
                      required: "Name is required",
                      minLength: {
                        value: 1,
                        message: "Name must be at least 1 character long",
                      },
                      maxLength: {
                        value: 100,
                        message: "Name must be less than 100 characters",
                      },
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField label="Description" error={errors?.description?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Enter description (optional)"
                    rows={4}
                    {...register("description", {
                      maxLength: {
                        value: 500,
                        message: "Description must be less than 500 characters",
                      },
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldSpace noMargin spaces={creatableSpaces} loading={spacesLoading} />

              <FormFieldWorkerPool name="workerPool" spaceFieldName="space" noMargin />

              <FormField
                label="Runner image"
                isOptional
                noMargin
                {...getIntentProjectRunnerImageTooltip()}
              >
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter runner image"
                    {...register("runnerImage", {
                      setValueAs: (value: string) => value.trim(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldTags label="Labels" tagName="label" name="labels" isOptional noMargin />
            </DrawerSection>
          </DrawerBody>
          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button
                type="submit"
                variant="primary"
                loading={isEditMode ? updateLoading : createLoading}
                disabled={!isValid || !isDirty}
              >
                {isEditMode ? "Update" : "Create"}
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showCreateIntentProjectDrawer = createDrawerTrigger(CreateIntentProjectDrawer);
